/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useQuery } from '@tanstack/react-query';

// Constants
import { queryKeys } from '@config/constants/query-keys';

// Lib
import { apiClient } from '@lib/api-client';
import { QueryConfig } from '@lib/react-query';

// Types
import { NOT_APPLICABLE } from '@config/constants/common';
import { QueueStatus } from '@config/constants/enum';
import { ROLES } from '@config/constants/roles';
import { TParamsGetApi } from '@global-types/index';
import { useAuthorization } from '@lib/authorization';
import { getCurrencyUnit } from '@utils/currency-units';
import {
	formatNumberWithSpaces,
	formatTwoDecimals,
} from '@utils/formats';
import { formatDate } from '@utils/functions';
import { getUnitLabel } from '@utils/units';
import Decimal from 'decimal.js';
import { TApiResponseGetLatestOffsets } from '../types';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const getLatestOffsets = (
	params: Partial<TParamsGetApi>
): Promise<TApiResponseGetLatestOffsets> => {
	return apiClient.get('/offsets', {
		params: {
			sort: params.sortParams,
			...params.queryParams,
			populate: [
				'organization',
				'entity',
				'tags',
				'certificates',
				'initiator',
			],
		},
	});
};

type UseLatestOffsetsOptions = {
	queryConfig?: QueryConfig<typeof getLatestOffsets>;
	queryParams: Record<string, any>;
	sortParams?: string;
};
/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */

export const useLatestOffsetsQuery = ({
	queryConfig,
	queryParams,
	sortParams,
}: UseLatestOffsetsOptions) => {
	const { checkRole, entity } = useAuthorization();

	const isProducer = checkRole([
		ROLES.PRODUCER_ADMIN,
		ROLES.PRODUCER_STAFF,
		ROLES.USER,
	]);

	const effectiveQueryParams = {
		...(entity
			? { ...queryParams, entity }
			: queryParams),
		query: { status: QueueStatus.SUCCESS },
	};

	const latestOffsetsQuery = useQuery({
		queryKey: [
			queryKeys.app.dashboard.latestOffsets.key,
			queryParams,
		],
		queryFn: () =>
			getLatestOffsets({
				queryParams: effectiveQueryParams,
				sortParams,
			}),
		enabled: !isProducer,
		...queryConfig,
		staleTime: 0,
	});

	const updateList = (offsets: any[]) =>
		offsets.reduce((acc: any[], currentItem: any) => {
			const quantityOffset = new Decimal(
				currentItem.quantity_gco2eq
			);

			const quantity = Decimal.div(
				quantityOffset.toNumber(),
				1000000
			);

			const modifiedItem: any = {
				...currentItem,
				brand:
					currentItem?.entity && currentItem.entity?.name
						? currentItem.entity?.name
						: NOT_APPLICABLE,
				quantity: `${formatTwoDecimals(
					quantity.toNumber()
				)} ${getUnitLabel('tco2e')}`,
				contributionDate: formatDate(
					currentItem.createdAt,
					'DD/MM/YYYY'
				),
				priceTTC:
					formatNumberWithSpaces(
						currentItem.amount_euro
					) +
					' ' +
					getCurrencyUnit('EUR'),
			};
			acc.push(modifiedItem);

			return acc;
		}, []);

	return {
		...latestOffsetsQuery,
		data: latestOffsetsQuery?.data?.rows
			? updateList(latestOffsetsQuery.data.rows)
			: [],
	};
};
